import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PurchaseInvoiceDetail } from '@miraki/contracts';
import { gt, money, ZERO } from '@miraki/money';
import { uploadPaymentProofFile, useSubmitPurchaseInvoicePayment } from '../../api/hooks';
import { invalidateAll } from '../../api/write-keys';
import { trimDecimal } from '../../format';
import { Alert, Button, Field, Panel } from '../../ui';

/**
 * R-27's "Pay this invoice" — CR-56 P4.
 *
 * 🔴 **What this does is tell Miraki a payment was made. It does not make one.** Sending the form
 * records a claim; the invoice, the amount outstanding and "available to order" are unchanged until
 * Admin confirms it, which is why the page says so before the retailer presses anything and says it
 * again afterwards, and why nothing here optimistically edits the invoice's figures (BG-06).
 *
 * Amount, date and bank reference go in one request; the proof file, if any, goes in a second, the
 * same way every other payment proof does. If the file fails the claim still stands — the retailer
 * is told, not left wondering whether anything was sent.
 *
 * `max` on the date and the amount ceiling are conveniences; the API checks both again.
 */
export function PayInvoiceForm({ inv }: { inv: PurchaseInvoiceDetail }) {
  const { t } = useTranslation();
  const submit = useSubmitPurchaseInvoicePayment(inv.id);
  const qc = useQueryClient();
  // The retailer states the date they paid, in their own calendar — the form only stops a future one.
  const today = new Date().toLocaleDateString('en-CA');
  const [amount, setAmount] = useState(trimDecimal(inv.outstanding));
  const [paidOn, setPaidOn] = useState(today);
  const [bankReference, setBankReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Bumped after a send so the (uncontrolled) file input is emptied too.
  const [fileKey, setFileKey] = useState(0);
  const [outcome, setOutcome] = useState<'sent' | 'sentNoFile' | 'failed' | null>(null);

  const wellFormed = /^\d+(\.\d{1,4})?$/.test(amount);
  const tooHigh = wellFormed && gt(money(amount), money(inv.outstanding));
  const positive = wellFormed && gt(money(amount), ZERO);
  const problem = !positive ? t('purchaseInvoice.pay.amountInvalid') : tooHigh ? t('purchaseInvoice.pay.amountTooHigh') : null;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(paidOn) && paidOn <= today;

  const send = async () => {
    setOutcome(null);
    try {
      const proof = await submit.mutateAsync({
        amount, currency: inv.currency, paidOn,
        ...(bankReference.trim() ? { bankReference: bankReference.trim() } : {}),
      });
      if (file) {
        const res = await uploadPaymentProofFile(proof.id, file);
        // The claim was written and the invoice re-read BEFORE the file went up, so that read says
        // "no proof". Read it again now, or "Payments you have sent" shows no attachment until a reload.
        await invalidateAll(qc, [['shop', 'payables']]);
        if (!res.ok) { setOutcome('sentNoFile'); return; }
      }
      setOutcome('sent');
      // Every field goes back to its starting value, so the form is ready for the next payment and
      // nothing that was just sent looks as if it still needs sending.
      setAmount(trimDecimal(inv.outstanding));
      setPaidOn(today);
      setBankReference('');
      setFile(null);
      setFileKey((k) => k + 1);
    } catch {
      setOutcome('failed');
    }
  };

  return (
    <Panel title={t('purchaseInvoice.pay.title')} pad>
      <p className="mk-hint">{t('purchaseInvoice.pay.intro')}</p>
      {outcome === 'sent' ? <Alert tone="us">{t('purchaseInvoice.pay.sent')}</Alert> : null}
      {outcome === 'sentNoFile' ? <Alert tone="them">{t('purchaseInvoice.pay.sentNoFile')}</Alert> : null}
      {outcome === 'failed' ? <Alert tone="stop">{t('purchaseInvoice.pay.failed')}</Alert> : null}
      <Field label={t('purchaseInvoice.pay.amount')} hint={problem ?? undefined}>
        <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t('purchaseInvoice.pay.paidOn')}>
        <input type="date" value={paidOn} max={today} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>
      <Field label={t('purchaseInvoice.pay.bankReference')} hint={t('purchaseInvoice.pay.bankReferenceHint')}>
        <input value={bankReference} maxLength={64} onChange={(e) => setBankReference(e.target.value)} />
      </Field>
      <Field label={t('purchaseInvoice.pay.proof')} hint={t('purchaseInvoice.pay.proofHint')}>
        <input
          key={fileKey} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </Field>
      <Button disabled={problem !== null || !dateOk || submit.isPending} onClick={() => { void send(); }}>
        {submit.isPending ? t('purchaseInvoice.pay.sending') : t('purchaseInvoice.pay.send')}
      </Button>
    </Panel>
  );
}
